import { useMemo } from 'react';
import { SkillRecord } from '@components/Component/Lesson/LessonContainer/Types';
import { StandardRecord } from '@components/ContentFilters/types';
import { LEARNOSITY_AUTOSCORE_TYPES } from '@utils/constants';
import {
  CHALKTALK,
  DISTRICT,
  MY_QUESTIONS,
} from '@components/LearnosityLessonReview/LessonReviewSetup/ItemBankToggleButtonGroup';
import { useUser } from '@reducers/user/hooks';
import { useCourseById } from '@reducers/courses/hooks';
import { useParams } from 'react-router-dom';

interface UseAlgoliaFiltersProps {
  selectedStandards?: StandardRecord[];
  selectedSkills?: SkillRecord[];
  selectedBloomsTaxonomy: string[];
  selectedDepthOfKnowledge: string[];
  questionSource: string;
  passageReference?: string;
  readability: string[];
  grade: string[];
  selectedQuestionType: string[];
  subject?: string;
  showUnaligned?: boolean;
}

/**
 * Custom hook for generating Algolia filters based on selected criteria
 */
const useAlgoliaFilters = ({
  selectedStandards = [],
  selectedSkills = [],
  selectedBloomsTaxonomy = [],
  selectedDepthOfKnowledge = [],
  questionSource,
  passageReference,
  readability,
  grade,
  selectedQuestionType,
  subject,
  showUnaligned = false,
}: UseAlgoliaFiltersProps) => {
  // Build facet filters based on selected criteria
  const user = useUser();
  const { courseId } = useParams();
  const courseDetails = useCourseById(courseId);
  const districtId = courseDetails?.organization?.district?.id;

  const facetFilters = useMemo(() => {
    const filters: string[][] = [];

    // Author filter
    if (questionSource === MY_QUESTIONS) {
      filters.push([`Author ID:${user.id}`]);
    } else if (questionSource === DISTRICT) {
      filters.push([`District ID:${districtId}`]);
    } else if (questionSource === CHALKTALK) {
      filters.push([`Author:Chalktalk`, `Author:CT-Navid`]);
    }

    // Standards filter
    if (selectedStandards.length > 0) {
      const standardFilters: string[] = [];
      selectedStandards.forEach((standard) => {
        if (standard.ab_guid) {
          standardFilters.push(`AB GUID:${standard.ab_guid}`);
        } else {
          // This might be undesirable at some point, as two states can create codes that overlap
          // For now, the greater concern is whether we might accidentally miss some data that doesn't have an ab_guid, but is tagged in our content
          standardFilters.push(`Standard:${standard.code}`);
        }
      });
      if (standardFilters.length > 0) {
        filters.push(standardFilters);
      }
    }

    // Skills filter
    const skillFilters: string[] = [];
    if (selectedSkills.length > 0) {
      selectedSkills.forEach((skill) => {
        skillFilters.push(`Skill:${skill.slug}`);
      });
      filters.push(skillFilters);
    } else if (!showUnaligned) {
      filters.push([`Skill:-unaligned`]);
    }

    // Blooms Taxonomy filter
    if (selectedBloomsTaxonomy.length > 0) {
      filters.push(selectedBloomsTaxonomy.map((blooms) => `Blooms Taxonomy:${blooms}`));
    }

    // Depth of Knowledge filter
    if (selectedDepthOfKnowledge.length > 0) {
      filters.push(selectedDepthOfKnowledge.map((dok) => `Depth Of Knowledge:${dok}`));
    }

    // Passage Reference filter
    if (passageReference) {
      filters.push([`Passage Reference:${passageReference}`]);
    }

    // Readability filter
    if (readability.length > 0) {
      filters.push(readability.map((read) => `metadata.lexile:${read}`));
    }

    // Grade filter
    if (grade.length > 0) {
      filters.push(grade.map((g) => `metadata.flesch_kincaid:${g}`));
    }

    // Question Type filter, if none selected, default to use all AUTOSCORABLE types only
    if (selectedQuestionType.length > 0) {
      filters.push(
        selectedQuestionType.map((questionType) => `Question Type:${questionType}`),
      );
    } else {
      filters.push(
        LEARNOSITY_AUTOSCORE_TYPES.map((questionType) => `Question Type:${questionType}`),
      );
    }

    // Subject filter
    if (subject) {
      filters.push([`Subject:${subject.toUpperCase()}`]);
    }

    return filters;
  }, [
    questionSource,
    selectedStandards,
    selectedSkills,
    showUnaligned,
    selectedBloomsTaxonomy,
    selectedDepthOfKnowledge,
    passageReference,
    readability,
    grade,
    selectedQuestionType,
    subject,
    user.id,
    districtId,
  ]);

  return facetFilters;
};

export default useAlgoliaFilters;
